/**
 * Apollo cache field policies; every connection read with `fetchMore` needs a
 * `relayStylePagination` entry here, or the next page never reaches the observed query.
 */
import type { FieldPolicy, Reference, StoreObject, TypePolicies } from '@apollo/client/cache';
import { relayStylePagination } from '@apollo/client/utilities';

/** Always replace cached value with incoming — used for non-normalized fields without stable IDs. */
const replacePolicy: FieldPolicy = {
  merge(_existing, incoming) {
    return incoming;
  }
};

/**
 * Id-less list items matched by `key`: the incoming list wins, and an item keeps the fields
 * another selection of the same item wrote.
 */
function mergeListBy(key: string): FieldPolicy<ReadonlyArray<StoreObject | Reference>> {
  return {
    merge(existing, incoming, { readField, mergeObjects }) {
      if (!existing) return incoming;
      return incoming.map(item => {
        const match = existing.find(old => readField(key, old) === readField(key, item));
        return match ? mergeObjects(match, item) : item;
      });
    }
  };
}

export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      // Relay-style cursor pagination — merges pages per filter in the cache,
      // so fetchMore needs no updateQuery and tab switches reuse cached pages.
      invoicesConnection: relayStylePagination(['filter', 'orderBy', 'direction']),
      documentsConnection: relayStylePagination(['filter']),
      integrationActivity: relayStylePagination(['filter']),
      syncRuns: relayStylePagination(['filter']),
      pendingEntityLinks: relayStylePagination(['filter']),
      entityLinks: relayStylePagination(['instanceId', 'filter']),
      targetDuplicateGroups: relayStylePagination(['instanceId', 'orderBy', 'direction']),
      // A connector's records missing at the source, and its held batches (the monitor's open ones).
      missingEntityLinks: relayStylePagination(['instanceId']),
      syncMissingHolds: relayStylePagination(['instanceId', 'filter']),
      // Drained to the last page by its hook — every screen reading it renders the whole list.
      targetConnections: relayStylePagination(['filter']),
      partiesConnection: relayStylePagination(['filter']),
      accountRelationsConnection: relayStylePagination([]),
      // The client detail narrows to one client — its pages must not merge into the matrix's.
      accountRelationAssignmentsConnection: relayStylePagination(['filter']),
      accountUsersConnection: relayStylePagination(['orderBy', 'direction']),
      masterAccountMembers: relayStylePagination(['orderBy', 'direction']),
      masterAccountAccountants: relayStylePagination(['orderBy', 'direction']),
      masterAccountInvitations: relayStylePagination(['orderBy', 'direction']),
      // The office's integration templates, one list per system filter.
      integrationTemplates: relayStylePagination(['pluginId', 'orderBy', 'direction']),
      // Sort args live outside `filter` here — omitting them would merge
      // differently-sorted pages into one cache entry.
      requisitionsConnection: relayStylePagination(['filter', 'orderBy', 'direction']),
      purchaseOrdersConnection: relayStylePagination(['filter', 'orderBy', 'direction']),
      goodsReceiptsConnection: relayStylePagination(['filter', 'orderBy', 'direction']),
      contractsConnection: relayStylePagination(['filter', 'orderBy', 'direction']),
      // Accounting code lists under Číselníky in System settings, paged with "load more".
      glPostings: relayStylePagination(['filter', 'orderBy', 'direction']),
      vatCodes: relayStylePagination(['filter', 'orderBy', 'direction']),
      vatPostings: relayStylePagination(['filter', 'orderBy', 'direction']),
      // Mailbox settings lists. No filter arg, but the sort args still key the entry, so a
      // sort toggle added later cannot merge differently-ordered pages into one.
      quarantinedEmailsConnection: relayStylePagination(['orderBy', 'direction']),
      mailSenderAllowlistConnection: relayStylePagination(['orderBy', 'direction']),
      // Candidate lines for pairing by hand: one list per invoice and view (own order, the others).
      invoiceMatchCandidateLines: relayStylePagination([
        'invoiceId',
        'purchaseOrderId',
        'excludePurchaseOrderId'
      ]),
      // The current user's inbox; no args besides the cursor, so one cache entry per session.
      inboxNotifications: relayStylePagination([]),
      // Question threads: the per-document list and the open-questions overview key on the filter,
      // a thread's messages on the thread.
      documentThreads: relayStylePagination(['filter']),
      documentThreadMessages: relayStylePagination(['threadId']),
      // The payer's credit statement; the request context names the payer, so no args key it.
      creditTransactions: relayStylePagination([]),
      // A party's bank accounts, one list per filter (the default hides inactive and terminal ones),
      // and one account's history.
      partyBankAccounts: relayStylePagination(['partyId', 'filter', 'orderBy', 'direction']),
      partyBankAccountEvents: relayStylePagination(['bankAccountId']),
      // Bank movements, one list per filter and sort.
      bankMovements: relayStylePagination(['filter', 'orderBy', 'direction']),
      // The open payments one statement line may be matched to by hand, one list per line.
      manualMatchCandidates: relayStylePagination(['bankMovementId']),
      // The invoices a payment from that line may pay, one list per line and supplier.
      queuePaymentInvoices: relayStylePagination(['bankMovementId', 'partyId']),
      // Payables and payment runs, one list per filter and sort.
      payableInvoices: relayStylePagination(['filter', 'orderBy', 'direction']),
      paymentRuns: relayStylePagination(['filter', 'orderBy', 'direction']),
      // Uploaded statement files, newest first; no filter, the sort args key the entry.
      bankStatementImports: relayStylePagination(['orderBy', 'direction']),
      // Id-less: the card and the buy-pack modal read the same offer, merged with its packs.
      creditPackOffer: { merge: true },
      // One history row per user, refetched network-only — the incoming list is always
      // authoritative, including the empty one a clear produces.
      chatHistory: replacePolicy
    }
  },
  // Id-less objects that several queries select with different fields: merge them, or each
  // write replaces the other's fields and the two queries refetch each other without end.
  User: {
    fields: {
      currentPreferences: { merge: true },
      preferences: { merge: true }
    }
  },
  // The packs on offer, paged with "load more" in the buy-pack modal.
  CreditPackOffer: {
    fields: {
      packs: relayStylePagination([])
    }
  },
  // The plugins of a bridge the account may use: the picker's list and the choices of one plugin.
  AvailableBridge: {
    fields: {
      plugins: mergeListBy('name')
    }
  },
  Party: {
    fields: {
      // Sent whole and replaced by the server; a shrinking list would otherwise warn.
      contactPersons: replacePolicy,
      bankAccounts: replacePolicy,
      syncProvenance: replacePolicy,
      // Non-normalized objects read with different selections (list vs detail): merge fields.
      mainContact: { merge: true },
      mainAddress: { merge: true },
      postalAddress: { merge: true },
      preferences: { merge: true },
      deliveryAddress: { merge: true }
    }
  },
  // Id-less usage, read with fewer fields by the contract list than by the detail: merge fields.
  Contract: {
    fields: {
      usage: { merge: true }
    }
  },
  // Id-less pairings, always read through one fragment: the incoming list wins.
  BankMovement: {
    fields: {
      erpPairings: replacePolicy
    }
  },
  // A run is read by the list and the detail with different selections, and its lists shrink when
  // an edit drops a payment or an allocation: the server's list is the whole truth.
  PaymentRun: {
    fields: {
      selectionCriteria: { merge: true },
      viewerCan: { merge: true },
      payments: replacePolicy,
      editors: replacePolicy,
      exports: replacePolicy
    }
  },
  Payment: {
    fields: {
      allocations: replacePolicy,
      cancellationClauses: replacePolicy,
      creditNotesToNet: replacePolicy
    }
  },
  PaymentAllocation: {
    fields: {
      confirmations: replacePolicy,
      notPayableReasons: replacePolicy
    }
  },
  // The id-less links of a synced row whose source lost it: the server's list is the whole truth.
  CostCenter: { fields: { syncProvenance: replacePolicy } },
  OrgUnit: { fields: { syncProvenance: replacePolicy } },
  Job: { fields: { syncProvenance: replacePolicy } },
  Product: { fields: { syncProvenance: replacePolicy } },
  Invoice: {
    fields: {
      // Id-less arrays/objects: always replace, or Apollo warns about cache data loss when a
      // mutation returns a different number of items.
      vatSummary: replacePolicy,
      invoiceLines: replacePolicy,
      bankingDetails: replacePolicy,
      supplierContact: replacePolicy,
      supplierAddress: replacePolicy,
      customerContact: replacePolicy,
      customerAddress: replacePolicy,
      validationResult: replacePolicy,
      // Keep a known document when a shallower query (the invoice list picker) returns null
      // for it; a list query must never erase what the detail read.
      document: {
        merge(existing, incoming) {
          if (incoming == null) return existing ?? incoming;
          return incoming;
        }
      }
    }
  }
};
